import type { Locale } from '../index'

/**
 * Page-body copy for the three marketing legal pages — Privacy, Terms and
 * Cookies — in all three locales (en / zh-Hant / zh-Hans). zh-Hant and zh-Hans
 * are distinct, hand-written translations — never auto-converted from one to the
 * other.
 *
 * This is placeholder copy that wires up the pages; final legal wording is owned
 * by the Geo4Logistics legal team. Translations stay faithful to the placeholder
 * text and introduce no new clauses or obligations.
 */
export const legal: Record<Locale, Record<string, string>> = {
  en: {
    // Privacy
    'legal.privacy.title': 'Privacy Policy',
    'legal.privacy.head.description': 'How Geo4Logistics collects, uses and protects your data.',
    'legal.privacy.h1': 'Privacy policy',
    'legal.privacy.intro': 'How we collect, use and protect the information you share with us.',
    'legal.privacy.updated': 'Last updated: placeholder — pending legal review.',
    'legal.privacy.note':
      'This is placeholder copy to wire up the page. Final privacy wording is owned by the Geo4Logistics legal team.',
    'legal.privacy.collect.heading': 'Information we collect',
    'legal.privacy.collect.body':
      'When you request a quote, contact us or use the customer portal, we collect the details you provide (such as your name, company, email, and shipment information) and basic technical data needed to operate the site.',
    'legal.privacy.use.heading': 'How we use it',
    'legal.privacy.use.item1': 'To respond to quote requests and enquiries.',
    'legal.privacy.use.item2':
      'To provide and improve our freight-forwarding services and the portal.',
    'legal.privacy.use.item3':
      'To remember your language preference and, with consent, run live chat and analytics.',
    'legal.privacy.cookies.heading': 'Cookies',
    'legal.privacy.cookies.body.pre':
      'We use essential cookies to run the site and remember preferences. Optional cookies (live chat, analytics) load only after you accept them — see our',
    'legal.privacy.cookies.body.link': 'cookie policy',
    'legal.privacy.cookies.body.post': '.',
    'legal.privacy.rights.heading': 'Your rights & contact',
    'legal.privacy.rights.body.pre':
      'You can request access to, correction of, or deletion of your data. Reach us at',
    'legal.privacy.rights.body.post': '.',

    // Terms
    'legal.terms.title': 'Terms of Service',
    'legal.terms.head.description':
      'The terms that govern your use of the Geo4Logistics website and services.',
    'legal.terms.h1': 'Terms of service',
    'legal.terms.intro': 'The terms that govern your use of this website and our services.',
    'legal.terms.updated': 'Last updated: placeholder — pending legal review.',
    'legal.terms.note':
      'This is placeholder copy to wire up the page. Final terms are owned by the Geo4Logistics legal team.',
    'legal.terms.use.heading': 'Use of this site',
    'legal.terms.use.body':
      "This website provides information about Geo4Logistics' freight-forwarding services. Quotes shown or requested through the site are indicative until confirmed in writing.",
    'legal.terms.services.heading': 'Services',
    'legal.terms.services.body':
      'Freight services are provided under our standard trading conditions, supplied with each booking confirmation. Transit times and rates are estimates and subject to carrier schedules, customs and other factors outside our control.',
    'legal.terms.liability.heading': 'Liability',
    'legal.terms.liability.body':
      'To the extent permitted by law, the site is provided “as is”. Our liability for the carriage of goods is governed by the applicable trading conditions and conventions.',
    'legal.terms.contact.heading': 'Contact',
    'legal.terms.contact.body.pre': 'Questions about these terms? Email',
    'legal.terms.contact.body.mid': 'or',
    'legal.terms.contact.body.link': 'contact us',
    'legal.terms.contact.body.post': '.',

    // Cookies
    'legal.cookies.title': 'Cookie Policy',
    'legal.cookies.head.description':
      'How Geo4Logistics uses cookies and how you can control them.',
    'legal.cookies.h1': 'Cookie policy',
    'legal.cookies.intro': 'What cookies we use, why, and how you can control them.',
    'legal.cookies.updated': 'Last updated: placeholder — pending legal review.',
    'legal.cookies.note':
      'This is placeholder copy to wire up the page. Final cookie wording is owned by the Geo4Logistics legal team.',
    'legal.cookies.essential.heading': 'Essential cookies',
    'legal.cookies.essential.body':
      'Required to run the site and remember basic preferences such as your chosen language. These are always on.',
    'legal.cookies.optional.heading': 'Optional cookies',
    'legal.cookies.optional.chat.label': 'Live chat',
    'legal.cookies.optional.chat.body':
      ' — when you accept, our chat tool sets cookies so we can hold a conversation with you.',
    'legal.cookies.optional.analytics.label': 'Analytics',
    'legal.cookies.optional.analytics.body':
      ' — privacy-respecting usage analytics, only if enabled and accepted.',
    'legal.cookies.manage.heading': 'Managing your choice',
    'legal.cookies.manage.body.pre':
      "You choose whether to accept optional cookies in the consent banner. You can change your mind any time by clearing this site's data in your browser, which will show the banner again. See our",
    'legal.cookies.manage.body.link': 'privacy policy',
    'legal.cookies.manage.body.post': ' for more.',
  },
  'zh-Hant': {
    // Privacy
    'legal.privacy.title': '隱私權政策',
    'legal.privacy.head.description': 'Geo4Logistics 如何蒐集、使用並保護您的資料。',
    'legal.privacy.h1': '隱私權政策',
    'legal.privacy.intro': '本公司如何蒐集、使用並保護您與我們分享的資訊。',
    'legal.privacy.updated': '最後更新：佔位內容 — 待法務審閱。',
    'legal.privacy.note':
      '此為用於建置頁面的佔位內容。最終隱私權條文由 Geo4Logistics 法務團隊負責。',
    'legal.privacy.collect.heading': '我們蒐集的資訊',
    'legal.privacy.collect.body':
      '當您索取報價、聯絡我們或使用客戶平台時，我們會蒐集您所提供的資料（例如您的姓名、公司、電子郵件及貨件資訊），以及維持網站運作所需的基本技術資料。',
    'legal.privacy.use.heading': '我們如何使用',
    'legal.privacy.use.item1': '回應報價需求與相關詢問。',
    'legal.privacy.use.item2': '提供並改善本公司的貨運代理服務與客戶平台。',
    'legal.privacy.use.item3': '記住您偏好的語言，並在取得您同意後啟用線上客服與分析。',
    'legal.privacy.cookies.heading': 'Cookie',
    'legal.privacy.cookies.body.pre':
      '我們使用必要 Cookie 維持網站運作並記住偏好設定。選用性 Cookie（線上客服、分析）僅在您接受後才會載入 — 詳見我們的',
    'legal.privacy.cookies.body.link': 'Cookie 政策',
    'legal.privacy.cookies.body.post': '。',
    'legal.privacy.rights.heading': '您的權利與聯絡方式',
    'legal.privacy.rights.body.pre':
      '您可要求存取、更正或刪除您的個人資料。歡迎透過以下方式與我們聯絡：',
    'legal.privacy.rights.body.post': '。',

    // Terms
    'legal.terms.title': '服務條款',
    'legal.terms.head.description': '規範您使用 Geo4Logistics 網站與服務的條款。',
    'legal.terms.h1': '服務條款',
    'legal.terms.intro': '規範您使用本網站與本公司服務的條款。',
    'legal.terms.updated': '最後更新：佔位內容 — 待法務審閱。',
    'legal.terms.note': '此為用於建置頁面的佔位內容。最終條款由 Geo4Logistics 法務團隊負責。',
    'legal.terms.use.heading': '本網站之使用',
    'legal.terms.use.body':
      '本網站提供有關 Geo4Logistics 貨運代理服務的資訊。透過本網站顯示或索取的報價，在以書面確認之前僅供參考。',
    'legal.terms.services.heading': '服務',
    'legal.terms.services.body':
      '貨運服務依本公司標準交易條件提供，並隨各次預訂確認一併提供。運輸時間與費率均為估計值，且受承運人船期、海關及其他本公司無法掌控之因素影響。',
    'legal.terms.liability.heading': '責任',
    'legal.terms.liability.body':
      '在法律允許的範圍內，本網站係以「現狀」提供。本公司對貨物運送的責任，依適用之交易條件與公約規範。',
    'legal.terms.contact.heading': '聯絡我們',
    'legal.terms.contact.body.pre': '對本條款有任何疑問嗎？請來信',
    'legal.terms.contact.body.mid': '，或',
    'legal.terms.contact.body.link': '聯絡我們',
    'legal.terms.contact.body.post': '。',

    // Cookies
    'legal.cookies.title': 'Cookie 政策',
    'legal.cookies.head.description': 'Geo4Logistics 如何使用 Cookie，以及您如何加以控管。',
    'legal.cookies.h1': 'Cookie 政策',
    'legal.cookies.intro': '我們使用哪些 Cookie、為何使用，以及您如何加以控管。',
    'legal.cookies.updated': '最後更新：佔位內容 — 待法務審閱。',
    'legal.cookies.note':
      '此為用於建置頁面的佔位內容。最終 Cookie 條文由 Geo4Logistics 法務團隊負責。',
    'legal.cookies.essential.heading': '必要 Cookie',
    'legal.cookies.essential.body':
      '維持網站運作並記住基本偏好（例如您所選擇的語言）所必需，這些 Cookie 一律啟用。',
    'legal.cookies.optional.heading': '選用性 Cookie',
    'legal.cookies.optional.chat.label': '線上客服',
    'legal.cookies.optional.chat.body':
      ' — 在您接受後，我們的客服工具會設定 Cookie，以便與您進行對話。',
    'legal.cookies.optional.analytics.label': '分析',
    'legal.cookies.optional.analytics.body':
      ' — 尊重隱私的使用情形分析，僅在啟用並獲您接受時才會運作。',
    'legal.cookies.manage.heading': '管理您的選擇',
    'legal.cookies.manage.body.pre':
      '您可在同意橫幅中選擇是否接受選用性 Cookie。您可隨時於瀏覽器中清除本網站的資料以改變決定，屆時將再次顯示該橫幅。詳情請參閱我們的',
    'legal.cookies.manage.body.link': '隱私權政策',
    'legal.cookies.manage.body.post': '。',
  },
  'zh-Hans': {
    // Privacy
    'legal.privacy.title': '隐私政策',
    'legal.privacy.head.description': 'Geo4Logistics 如何收集、使用并保护您的数据。',
    'legal.privacy.h1': '隐私政策',
    'legal.privacy.intro': '本公司如何收集、使用并保护您与我们分享的信息。',
    'legal.privacy.updated': '最后更新：占位内容 — 待法务审核。',
    'legal.privacy.note': '这是用于搭建页面的占位内容。最终隐私条款由 Geo4Logistics 法务团队负责。',
    'legal.privacy.collect.heading': '我们收集的信息',
    'legal.privacy.collect.body':
      '当您索取报价、联系我们或使用客户平台时，我们会收集您所提供的信息（例如您的姓名、公司、电子邮箱及货件信息），以及维持网站运行所需的基本技术数据。',
    'legal.privacy.use.heading': '我们如何使用',
    'legal.privacy.use.item1': '回应报价需求与相关咨询。',
    'legal.privacy.use.item2': '提供并改善本公司的货运代理服务与客户平台。',
    'legal.privacy.use.item3': '记住您偏好的语言，并在取得您同意后启用在线客服与分析。',
    'legal.privacy.cookies.heading': 'Cookie',
    'legal.privacy.cookies.body.pre':
      '我们使用必要 Cookie 维持网站运行并记住偏好设置。可选 Cookie（在线客服、分析）仅在您接受后才会加载 — 详见我们的',
    'legal.privacy.cookies.body.link': 'Cookie 政策',
    'legal.privacy.cookies.body.post': '。',
    'legal.privacy.rights.heading': '您的权利与联系方式',
    'legal.privacy.rights.body.pre':
      '您可要求访问、更正或删除您的个人信息。欢迎通过以下方式联系我们：',
    'legal.privacy.rights.body.post': '。',

    // Terms
    'legal.terms.title': '服务条款',
    'legal.terms.head.description': '规范您使用 Geo4Logistics 网站与服务的条款。',
    'legal.terms.h1': '服务条款',
    'legal.terms.intro': '规范您使用本网站与本公司服务的条款。',
    'legal.terms.updated': '最后更新：占位内容 — 待法务审核。',
    'legal.terms.note': '这是用于搭建页面的占位内容。最终条款由 Geo4Logistics 法务团队负责。',
    'legal.terms.use.heading': '本网站之使用',
    'legal.terms.use.body':
      '本网站提供有关 Geo4Logistics 货运代理服务的信息。通过本网站显示或索取的报价，在以书面确认之前仅供参考。',
    'legal.terms.services.heading': '服务',
    'legal.terms.services.body':
      '货运服务依本公司标准交易条件提供，并随每次订舱确认一并提供。运输时间与费率均为估计值，且受承运人船期、海关及其他本公司无法掌控之因素影响。',
    'legal.terms.liability.heading': '责任',
    'legal.terms.liability.body':
      '在法律允许的范围内，本网站以“现状”提供。本公司对货物运送的责任，依适用之交易条件与公约规范。',
    'legal.terms.contact.heading': '联系我们',
    'legal.terms.contact.body.pre': '对本条款有任何疑问吗？请来信',
    'legal.terms.contact.body.mid': '，或',
    'legal.terms.contact.body.link': '联系我们',
    'legal.terms.contact.body.post': '。',

    // Cookies
    'legal.cookies.title': 'Cookie 政策',
    'legal.cookies.head.description': 'Geo4Logistics 如何使用 Cookie，以及您如何加以控制。',
    'legal.cookies.h1': 'Cookie 政策',
    'legal.cookies.intro': '我们使用哪些 Cookie、为何使用，以及您如何加以控制。',
    'legal.cookies.updated': '最后更新：占位内容 — 待法务审核。',
    'legal.cookies.note':
      '这是用于搭建页面的占位内容。最终 Cookie 条款由 Geo4Logistics 法务团队负责。',
    'legal.cookies.essential.heading': '必要 Cookie',
    'legal.cookies.essential.body':
      '维持网站运行并记住基本偏好（例如您所选择的语言）所必需，这些 Cookie 始终启用。',
    'legal.cookies.optional.heading': '可选 Cookie',
    'legal.cookies.optional.chat.label': '在线客服',
    'legal.cookies.optional.chat.body':
      ' — 在您接受后，我们的客服工具会设置 Cookie，以便与您进行对话。',
    'legal.cookies.optional.analytics.label': '分析',
    'legal.cookies.optional.analytics.body':
      ' — 尊重隐私的使用情况分析，仅在启用并获您接受时才会运行。',
    'legal.cookies.manage.heading': '管理您的选择',
    'legal.cookies.manage.body.pre':
      '您可在同意横幅中选择是否接受可选 Cookie。您可随时在浏览器中清除本网站的数据以改变决定，届时将再次显示该横幅。详情请参阅我们的',
    'legal.cookies.manage.body.link': '隐私政策',
    'legal.cookies.manage.body.post': ' 了解更多。',
  },
}
